export { filterSortRows, normalizeCollectionQuery } from '../../shared/collection-query.mjs';

export const LIST_COLUMNS = Object.freeze([
  { id: 'select', label: 'Seçim', width: 38, min: 38, max: 38, fixed: true },
  { id: 'name', label: 'Fırsat', width: 236, min: 170, max: 700, fixed: true },
  { id: 'category', label: 'Kategori', width: 110, min: 90, max: 450 },
  { id: 'stage', label: 'Durum', width: 175, min: 135, max: 500 },
  { id: 'location', label: 'Şehir', width: 110, min: 90, max: 450 },
  { id: 'packageName', label: 'Paket', width: 160, min: 120, max: 700 },
  { id: 'startDate', label: 'Etkinlik tarihi', width: 180, min: 145, max: 450 },
  { id: 'amount', label: 'Bedel', width: 145, min: 120, max: 450 },
  { id: 'owner', label: 'Sorumlu', width: 170, min: 130, max: 600 },
  { id: 'nextAction', label: 'Sıradaki iş', width: 235, min: 160, max: 900 },
]);
const ids = LIST_COLUMNS.map(column => column.id);
const movable = ids.filter(id => !['select', 'name'].includes(id));
const isPlainObject = value => value && typeof value === 'object' && !Array.isArray(value);

export function defaultColumnPreferences() {
  return { version: 1, order: [...ids], visibility: { owner: false }, sizing: Object.fromEntries(LIST_COLUMNS.map(c => [c.id, c.width])) };
}
export function clampColumnWidth(id, value) {
  const column = LIST_COLUMNS.find(column => column.id === id);
  if (!column || !Number.isFinite(value)) return column?.width ?? 0;
  return Math.round(Math.min(column.max, Math.max(column.min, value)));
}
export function normalizeColumnPreferences(value) {
  const defaults = defaultColumnPreferences();
  if (!isPlainObject(value) || value.version !== 1 || !Array.isArray(value.order) || !isPlainObject(value.visibility) || !isPlainObject(value.sizing)) return defaults;
  const order = ['select', 'name', ...new Set(value.order.filter(id => movable.includes(id)))];
  for (const id of movable) if (!order.includes(id)) order.push(id);
  const visibility = { ...defaults.visibility };
  for (const id of movable) if (typeof value.visibility[id] === 'boolean') visibility[id] = value.visibility[id];
  const sizing = { ...defaults.sizing };
  for (const id of ids) if (Number.isFinite(value.sizing[id])) sizing[id] = clampColumnWidth(id, value.sizing[id]);
  return { version: 1, order, visibility, sizing };
}
export function readColumnPreferences(storage, key) {
  try { return normalizeColumnPreferences(JSON.parse(storage?.getItem(key) || 'null')); } catch { return defaultColumnPreferences(); }
}
export function writeColumnPreferences(storage, key, preferences) {
  try { storage?.setItem(key, JSON.stringify(normalizeColumnPreferences(preferences))); return !!storage; } catch { return false; }
}
export function visibleListColumns(preferences) {
  const valid = normalizeColumnPreferences(preferences);
  return valid.order.map(id => LIST_COLUMNS.find(column => column.id === id)).filter(column => column.fixed || valid.visibility[column.id] !== false);
}
export function moveListColumn(preferences, id, targetId) {
  const valid = normalizeColumnPreferences(preferences);
  if (!movable.includes(id) || !movable.includes(targetId) || id === targetId) return valid;
  const order = valid.order.filter(column => column !== id);
  order.splice(order.indexOf(targetId), 0, id);
  return { ...valid, order };
}
export function stepListColumn(preferences, id, direction) {
  const valid = normalizeColumnPreferences(preferences);
  if (!movable.includes(id) || ![-1, 1].includes(direction)) return valid;
  const index = valid.order.indexOf(id), target = index + direction;
  if (target < 2 || target >= valid.order.length) return valid;
  const order = [...valid.order]; [order[index], order[target]] = [order[target], order[index]];
  return { ...valid, order };
}
export function normalizeSelectedIds(selectedIds) {
  return [...new Set((selectedIds instanceof Set ? [...selectedIds] : Array.isArray(selectedIds) ? selectedIds : []).filter(id => typeof id === 'string'))];
}
export function selectionSummary(selectedIds, allRows, visibleRows) {
  const allowed = new Set(allRows.map(row => row.id));
  const selected = normalizeSelectedIds(selectedIds).filter(id => allowed.has(id));
  const selection = new Set(selected), visible = new Set(visibleRows.map(row => row.id));
  const visibleSelected = visibleRows.filter(row => selection.has(row.id)).length;
  return { selected, visibleSelected, hiddenCount: selected.filter(id => !visible.has(id)).length, all: visibleRows.length > 0 && visibleSelected === visibleRows.length, mixed: visibleSelected > 0 && visibleSelected < visibleRows.length };
}
export function toggleVisibleSelection(selectedIds, allRows, visibleRows, checked) {
  const selected = new Set(selectionSummary(selectedIds, allRows, visibleRows).selected);
  for (const row of visibleRows) if (checked) selected.add(row.id); else selected.delete(row.id);
  return [...selected];
}
export function readListScroll(storage, key) {
  try {
    const value = JSON.parse(storage?.getItem(key) || 'null');
    return { left: Number.isFinite(value?.left) ? Math.max(0, value.left) : 0, top: Number.isFinite(value?.top) ? Math.max(0, value.top) : 0 };
  } catch { return { left: 0, top: 0 }; }
}
export function writeListScroll(storage, key, value) {
  try { storage?.setItem(key, JSON.stringify({ left: Math.max(0, Number(value.left) || 0), top: Math.max(0, Number(value.top) || 0) })); return !!storage; } catch { return false; }
}
